import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { BadgePercent, Bell, PackageCheck, ShoppingBag } from "lucide-react";
import { useNotificacoes } from "shared/hooks/useNotificacoes";
import { NotificationProps } from "shared/services/api/notificacoes.api";
import { traduzirTopic } from "shared/utils/notificacoes";
import styles from "./Notificacao.module.scss";

const getIcon = (topic: NotificationProps["topic"]) => {
  switch (topic) {
    case "ORDER_CREATED":
    case "PURCHASE":
      return <PackageCheck size={22} />;
    case "ORDER_PAID":
    case "ORDER_SHIPPED":
    case "ORDER_DELIVERED":
    case "ORDER_CANCELLED":
    case "SALE":
      return <ShoppingBag size={22} />;
    case "PROMOTION":
      return <BadgePercent size={22} />;
    default:
      return <Bell size={22} />;
  }
};

const formatarData = (data: string) => {
  if (!data) return "";
  const dataNotificacao = new Date(data);
  if (Number.isNaN(dataNotificacao.getTime())) return data;
  return dataNotificacao.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRotaReferencia = (item: NotificationProps) => {
  if (!item.reference) return null;
  if (item.reference.type === "ORDER") {
    if (item.recipientType === "BUYER") return "/minhascompras";
    if (item.recipientType === "STORE_OWNER") return "/minhasvendas";
  }
  if (item.reference.type === "PRODUCT") {
    return `/produto/${item.reference.id}`;
  }
  return null;
};

const getLabelReferencia = (item: NotificationProps) => {
  if (!item.reference) return null;
  switch (item.reference.type) {
    case "ORDER":
      return `Pedido #${item.reference.id.slice(0, 8)}...`;
    case "PRODUCT":
      return "Ver produto";
    case "STORE":
      return "Ver loja";
    default:
      return null;
  }
};

interface Props {
  limite?: number;
  modo?: "popup" | "pagina";
}

export const Notificacao = ({ limite, modo = "popup" }: Props) => {
  const {
    notifications,
    marcarComoLida,
    carregarMaisNotificacoes,
    loadingMore,
    hasMore,
  } = useNotificacoes();

  const navigate = useNavigate();

  const [lidasLocalmente, setLidasLocalmente] = useState<Set<string>>(new Set());
  const [animandoLeitura, setAnimandoLeitura] = useState<Set<string>>(new Set());

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const lista = limite ? notifications.slice(0, limite) : notifications;

  useEffect(() => {
    if (limite || modo !== "pagina" || !hasMore) return;

    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && !loadingMore) {
          carregarMaisNotificacoes();
        }
      },
      {
        root: null,
        rootMargin: "300px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [limite, modo, hasMore, loadingMore, carregarMaisNotificacoes]);

  const marcarNotificacaoComoLida = (id: string) => {
    setLidasLocalmente((prev) => new Set(prev).add(id));
    setAnimandoLeitura((prev) => new Set(prev).add(id));
    marcarComoLida(id);
  };

  const handleCliqueNotificacao = (item: NotificationProps) => {
    if (!item.read && !lidasLocalmente.has(item.id)) {
      marcarNotificacaoComoLida(item.id);
    }
  };

  const handleDuploCliqueNotificacao = (item: NotificationProps) => {
    if (!item.read && !lidasLocalmente.has(item.id)) {
      marcarNotificacaoComoLida(item.id);
    }

    if (modo === "popup") {
      navigate("/todasnotificacoes");
    }
  };

  const handleCliqueReferencia = (
    event: React.MouseEvent,
    item: NotificationProps
  ) => {
    event.stopPropagation();

    if (!item.read && !lidasLocalmente.has(item.id)) {
      marcarNotificacaoComoLida(item.id);
    }
  };

  if (lista.length === 0) {
    return (
      <div className={`${styles.allContain} ${modo === "popup" ? styles.popup : ""}`}>
        <div className={styles.vazio}>
          <Bell size={28} />
          <p>Nenhuma notificação encontrada.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.allContain} ${modo === "popup" ? styles.popup : ""}`}>
      {lista.map((item) => {
        const rotaReferencia = getRotaReferencia(item);
        const labelReferencia = getLabelReferencia(item);
        const foiLida = item.read || lidasLocalmente.has(item.id);
        const foiMarcadaAgora = animandoLeitura.has(item.id);

        return (
          <div
            key={item.id}
            className={`${styles.container} ${modo === "pagina" ? styles.containerPagina : ""} ${foiLida ? styles.lida : styles.naoLida} ${foiMarcadaAgora ? styles.marcadaComoLida : ""}`}
            onClick={() => handleCliqueNotificacao(item)}
            onDoubleClick={() => handleDuploCliqueNotificacao(item)}
          >
            <div className={styles.icon}>{getIcon(item.topic)}</div>

            <div className={styles.content}>
              <p className={styles.tipo}>{traduzirTopic(item.topic)}</p>

              <p className={styles.titulo}>{item.title}</p>

              <p className={styles.informacao}>{item.body}</p>

              {rotaReferencia && labelReferencia && (
                <Link
                  to={rotaReferencia}
                  className={styles.linkPedido}
                  title={
                    item.reference?.type === "ORDER"
                      ? `Pedido #${item.reference.id}`
                      : labelReferencia
                  }
                  onClick={(event) => handleCliqueReferencia(event, item)}
                >
                  {labelReferencia}
                </Link>
              )}

              {modo === "pagina" && (
                <p className={styles.tempoPagina}>
                  {formatarData(item.createdAt)}
                </p>
              )}
            </div>

            {!foiLida && <span className={styles.indicadorNaoLida} />}

            {foiLida && (
              <span className={styles.indicadorLida}>
                Lida
              </span>
            )}

            {modo === "popup" && (
              <p className={styles.tempo}>
                {formatarData(item.createdAt)}
              </p>
            )}
          </div>
        );
      })}

      {modo === "pagina" && !limite && hasMore && (
        <div ref={sentinelRef} className={styles.loadingMore}>
          {loadingMore && (
            <span>Carregando mais notificações...</span>
          )}
        </div>
      )}
    </div>
  );
};